import {
  MAX_COMMAND_DESCRIPTION_LENGTH,
  MAX_COMMAND_NAME_LENGTH,
  MAX_COMMAND_PROMPT_LENGTH,
  SLASH_COMMANDS_URL_PARAM,
} from './constants';
import { SlashCommand, SlashCommandSharePayload } from './types';

/** Validate that a command name uses only lowercase alphanumeric, hyphens, and underscores */
export function validateCommandName(name: string): string | null {
  if (!name) return 'Name is required';
  if (name.length > MAX_COMMAND_NAME_LENGTH)
    return `Name must be ${MAX_COMMAND_NAME_LENGTH} characters or fewer`;
  if (!/^[a-z0-9_-]+$/.test(name))
    return 'Only lowercase letters, numbers, hyphens, and underscores';
  return null;
}

/** Validate description length */
export function validateDescription(description: string): string | null {
  if (description.length > MAX_COMMAND_DESCRIPTION_LENGTH)
    return `Description must be ${MAX_COMMAND_DESCRIPTION_LENGTH} characters or fewer`;
  return null;
}

/** Validate prompt text */
export function validatePromptText(promptText: string): string | null {
  if (!promptText.trim()) return 'Prompt text is required';
  if (promptText.length > MAX_COMMAND_PROMPT_LENGTH)
    return `Prompt must be ${MAX_COMMAND_PROMPT_LENGTH} characters or fewer`;
  return null;
}

/** Encode commands into a base64url string for sharing */
export function encodeSharePayload(commands: SlashCommand[]): string {
  const payload: SlashCommandSharePayload = {
    v: 1,
    commands: commands.map(({ name, description, promptText }) => ({
      name,
      description,
      promptText,
    })),
  };
  const json = JSON.stringify(payload);
  const base64 = btoa(unescape(encodeURIComponent(json)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decode a base64url string back into a share payload */
export function decodeSharePayload(
  encoded: string,
): SlashCommandSharePayload | null {
  try {
    let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const json = decodeURIComponent(escape(atob(base64)));
    const payload = JSON.parse(json);
    if (payload.v === 1 && Array.isArray(payload.commands)) {
      return payload as SlashCommandSharePayload;
    }
    return null;
  } catch {
    return null;
  }
}

/** Build a full share URL with encoded commands */
export function buildShareUrl(commands: SlashCommand[]): string {
  const encoded = encodeSharePayload(commands);
  const url = new URL(window.location.href);
  // Clean existing params that we don't want in the share URL
  url.searchParams.delete(SLASH_COMMANDS_URL_PARAM);
  url.searchParams.set(SLASH_COMMANDS_URL_PARAM, encoded);
  return url.toString();
}
